import { Injectable } from '@angular/core';
import { ApiService } from '@core/api.service';
import { AuthService } from '@features/auth/auth.service';
import { TimeService } from '@services/common/time.service';
import { Collection, CollectionModel, CrudService, GetEntityModel, ItemModel, UpdateEntityModel } from 'collection';
import { endOfDay, format, startOfDay } from 'date-fns';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

@Injectable({ providedIn: 'root' })
export class SchedulerService {
  collection: CollectionModel;
  item: ItemModel;
  isOpenSidebar: boolean = true;

  schedulerPlannerCollection: CollectionModel;
  workersCollection: CollectionModel;
  unscheduledProjectsCollection: CollectionModel;

  publishWorks: UpdateEntityModel;
  workSettings: GetEntityModel;
  workSumHours: GetEntityModel;

  $reloadSumHours: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $showScheduler: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  fullScreenView$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _isOpenSidebar: BehaviorSubject<boolean> = new BehaviorSubject(this.isOpenSidebar);

  reloadCollection$ = new Subject();
  autoLunchCalculate: GetEntityModel;

  get $isOpenSidebar(): Observable<any> {
    return this._isOpenSidebar.asObservable();
  }

  toggleSidebar(): boolean {
    this.isOpenSidebar = !this.isOpenSidebar;
    this._isOpenSidebar.next(this.isOpenSidebar);
    return this.isOpenSidebar;
  }

  closeSidebar(): void {
    this.isOpenSidebar = false;
    this._isOpenSidebar.next(this.isOpenSidebar);
  }

  openSidebar(): void {
    this.isOpenSidebar = true;
    this._isOpenSidebar.next(this.isOpenSidebar);
  }

  setDefaultLunch({ settings, fromTime, toTime, lunchTime, lunchTouched }): Observable<number | null> {
    if (!settings.autolunch_enable || !settings.enable_lunch_registration) {
      return of(null);
    }

    if (fromTime && toTime && !lunchTime) {
      const fromTimeConverted = format(legacyParse(fromTime), convertTokens('HH:mm'));
      const toTimeConverted = format(legacyParse(toTime), convertTokens('HH:mm'));

      if (!lunchTouched) {
        return this.autoLunchCalculate.get({ query: { from_time: fromTimeConverted, to_time: toTimeConverted } }).pipe(
          map((res: { data: number }) => {
            return this._timeService.secondsToMinutes(res.data);
          })
        );
      }
    }
    return of(null);
  }

  private initSchedulerSubscription(): void {
    const showScheduler = !this._auth.user?.hide_scheduler;
    this.$showScheduler.next(showScheduler);

    this._auth.$user
      .pipe(
        map((user) => {
          this.$showScheduler.next(!user?.hide_scheduler);
        })
      )
      .subscribe();
  }

  timesheetDays: Array<any> = [];
  groupsTimesheetDays;

  initTimeSheetDays({ from, to }): void | boolean {
    if (!from && !to) {
      return false;
    }

    const periodDates = this._timeService.getPeriodDates(from, to);
    this.timesheetDays = periodDates.days;

    const groupsTimesheetDays = this.timesheetDays.reduce((acc, d) => {
      const keyObj = JSON.stringify({ month: d.month, year: d.year, week: d.week });

      if (acc[keyObj]) {
        acc[keyObj].push(d);
      } else {
        acc[keyObj] = [d];
      }
      return acc;
    }, []);

    this.groupsTimesheetDays = Object.entries(groupsTimesheetDays).sort(([keyA, valueA], [keyB, valueB]) => {
      const dateFrom: any = new Date(valueA[0].date);
      const dateTo: any = new Date(valueB[0].date);
      return dateFrom - dateTo;
    });
  }

  constructor(
    private _auth: AuthService,
    private _api: ApiService,
    private _crud: CrudService,
    private _timeService: TimeService
  ) {
    this.initSchedulerSubscription();

    this.publishWorks = this._crud.createUpdateEntity({ name: 'work/publish' });
    this.workSettings = this._crud.createGetEntity({ name: 'work-settings' });
    this.workSumHours = this._crud.createGetEntity({ name: 'work/sum-hours' });
    this.schedulerPlannerCollection = new Collection({
      api: this._crud.createEntity({ name: 'work' }),
      params: {
        expand: 'client,project'
      }
    });

    this.workersCollection = new Collection({
      api: this._api.workers,
      params: {
        status: 'active',
        expand: 'full_name,avatars,wages',
        sort: 'name',
        per_page: 30
      }
    });

    this.unscheduledProjectsCollection = new Collection({
      api: this._crud.createEntity({ name: 'project/unscheduled' }),
      params: {
        unscheduled_subprojects: true,
        expand: 'unscheduled_subprojects,client',
        sort: 'name'
      }
    });

    this.schedulerPlannerCollection.setParams('per_page', 9999);

    this.autoLunchCalculate = this._crud.createGetEntity({ name: 'auto-lunch/calculate' });
  }
}
